import { makeAddressLoginSafe } from './login-address';
import { Injectable } from '@angular/core';
import Keycloak from 'keycloak-js';
import { environment } from 'src/environments/environment';
import { UserRoleEnum } from 'src/app/models/common-model';

const ROLE_MAP: Record<string, string> = {
  admin: 'Administrator',
  'advanced-user': 'AdvancedUser',
  user: 'User',
};

/**
 * Realm roles that open the developer portal (Roommatik convention: `int-*` for partners and
 * hotel IT, `doc-*` for the internal team). They are independent of the panel roles above: a
 * partner usually has `int-templatify` and nothing else.
 */
export const DEVELOPER_REALM_ROLES = ['int-templatify', 'doc-templatify'];

@Injectable({ providedIn: 'root' })
export class SsoService {
  private instance: Keycloak | null = null;
  private refreshInterval: ReturnType<typeof setInterval> | null = null;

  async init(): Promise<boolean> {
    this.instance = new Keycloak({
      url: environment.authUrl,
      realm: environment.authRealm,
      clientId: environment.authClientId,
    });

    // The login sends the whole address as redirect_uri: make it one the identity provider accepts.
    makeAddressLoginSafe();

    const authenticated = await this.instance.init({
      onLoad: 'login-required',
      pkceMethod: 'S256',
      checkLoginIframe: false,
      silentCheckSsoRedirectUri:
        window.location.origin + '/silent-check-sso.html',
    });

    if (authenticated && this.instance.token) {
      this.persistAuthState();
      this.startTokenRefresh();
    }

    return authenticated;
  }

  get token(): string | undefined {
    return this.instance?.token;
  }

  get subject(): string | undefined {
    return this.instance?.tokenParsed?.['sub'] as string | undefined;
  }

  get email(): string | undefined {
    return this.instance?.tokenParsed?.['email'] as string | undefined;
  }

  get name(): string | undefined {
    return this.instance?.tokenParsed?.['name'] as string | undefined;
  }

  get role(): string {
    return localStorage.getItem('userRole') ?? '';
  }

  /** Realm roles of the token (`realm_access.roles`). */
  get realmRoles(): string[] {
    const access = this.instance?.tokenParsed?.['realm_access'] as { roles?: string[] } | undefined;
    return access?.roles ?? [];
  }

  /** Can open the developer portal: any panel user, or a holder of one of the documentation realm roles. */
  get hasDeveloperAccess(): boolean {
    return !!this.role || this.realmRoles.some((r) => DEVELOPER_REALM_ROLES.includes(r));
  }

  /** A partner / hotel IT account: documentation only, nothing else of the panel. */
  get isDeveloperOnly(): boolean {
    return !this.role && this.hasDeveloperAccess;
  }

  isAuthenticated(): boolean {
    return !!this.instance?.authenticated;
  }

  async updateToken(minValiditySeconds = 30): Promise<boolean> {
    if (!this.instance) return false;
    try {
      const refreshed = await this.instance.updateToken(minValiditySeconds);
      if (refreshed) this.persistAuthState();
      return true;
    } catch {
      this.logout();
      return false;
    }
  }

  logout(): void {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
    localStorage.clear();
    const redirectUri = environment.portalUrl;
    if (this.instance) {
      this.instance.logout({ redirectUri });
    } else {
      window.location.href = redirectUri;
    }
  }

  private persistAuthState(): void {
    if (!this.instance?.token || !this.instance.tokenParsed) return;

    localStorage.setItem('authToken', this.instance.token);
    localStorage.setItem('auth_method', 'sso');

    const resourceAccess = this.instance.tokenParsed['resource_access'] as
      | Record<string, { roles: string[] }>
      | undefined;

    const azp = this.instance.tokenParsed['azp'] as string | undefined;
    const clientKey = azp ?? environment.authClientId;

    const clientRoles = resourceAccess?.[clientKey]?.roles ?? [];
    const mapped = clientRoles
      .map((r) => ROLE_MAP[r])
      .filter((r): r is string => !!r);

    const role =
      mapped.find((r) => r === UserRoleEnum[UserRoleEnum.Administrator]) ??
      mapped.find((r) => r === UserRoleEnum[UserRoleEnum.AdvancedUser]) ??
      mapped.find((r) => r === UserRoleEnum[UserRoleEnum.User]) ??
      '';

    if (role) {
      localStorage.setItem('userRole', role);
    } else {
      localStorage.removeItem('userRole');
    }
  }

  private startTokenRefresh(): void {
    if (this.refreshInterval) clearInterval(this.refreshInterval);
    this.refreshInterval = setInterval(() => {
      this.updateToken(60);
    }, 30_000);
  }
}
